/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Backdrop, Badge, Box, Button, Card, Chip, Collapse, Container, Dialog, Divider, Fab, Fade, FormControl, Grid, InputAdornment, InputLabel,
    MenuItem, Select, Skeleton, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import { alpha, useTheme } from '@mui/material/styles';
import {
    Add, ArrowForward, Euro, ExpandLess, ExpandMore, HourglassBottom, KeyboardArrowUp, RocketLaunch, Search, TableRows, Timeline, Tune,
    ViewModule, WarningAmber,
} from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { getData } from "../../services/base.service";
import { pulseAnimation } from "../../styles/styles";
import ScrollTop from "../Navigation/ScrollTop";
import AutocompleteComponent from "../Shared/AutocompleteComponent";
import { ProjectListItem } from "../../types/projectTypes";
import ProjectCard from "./ProjectCard";
import ProjectTimeline from "./ProjectTimeline";
import ProjectDetails from "./ProjectDetails";
import AddProject from "./AddProject";
import { formatDate, formatMoney, formatPercent, panelSx, splitList } from "./projectUtils";

type ProjectsViewProps = {
    userRoles: string[];
};

const ACTIVE_STATUSES = ['Ongoing', 'Yet to start', 'On hold'];
type Scope = 'active' | 'closed' | 'all';
type SortKey = 'project_number' | 'project_end_date' | 'int_open_volume' | 'completion_ratio';
type ViewMode = 'cards' | 'table' | 'timeline';

const tableColumns: GridColDef[] = [
    { field: 'project_number', headerName: 'INT #', width: 90 },
    { field: 'project_name', headerName: 'Name', flex: 2, minWidth: 220 },
    { field: 'client', headerName: 'Client', width: 110 },
    { field: 'countries', headerName: 'Country', width: 140 },
    { field: 'status', headerName: 'Status', width: 110 },
    { field: 'project_condition', headerName: 'Condition', width: 150 },
    { field: 'lead', headerName: 'Role', width: 90, valueGetter: (params) => (params.row.lead ? 'Lead' : 'Partner') },
    { field: 'project_directors', headerName: 'PD', width: 120 },
    { field: 'project_managers', headerName: 'PM', width: 120 },
    { field: 'project_end_date', headerName: 'End', width: 110, valueFormatter: (params) => formatDate(params.value as string) },
    { field: 'contract_volume', headerName: 'Volume', width: 120, type: 'number', valueFormatter: (params) => formatMoney(params.value as number) },
    { field: 'completion_ratio', headerName: 'Invoiced', width: 100, type: 'number', valueFormatter: (params) => formatPercent(params.value as number) },
    { field: 'int_open_volume', headerName: 'Open INT', width: 120, type: 'number', valueFormatter: (params) => formatMoney(params.value as number) },
    { field: 'currency', headerName: 'Cur.', width: 70 },
];

type KpiTileProps = {
    label: string;
    value: string;
    sub?: string;
    Icon: React.ElementType;
    onClick?: () => void;
    highlight?: boolean;
};

function KpiTile({ label, value, sub, Icon, onClick, highlight }: KpiTileProps) {
    const theme = useTheme();
    const tone = highlight ? theme.palette.warning.main : theme.palette.primary.main;
    return (
        <Card elevation={0} onClick={onClick} sx={{
            ...panelSx(theme), height: '100%', cursor: onClick ? 'pointer' : 'default', transition: 'border-color 0.2s ease',
            '&:hover': onClick ? { borderColor: alpha(tone, 0.5) } : undefined,
        }}>
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ padding: 2 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: alpha(tone, 0.12), color: tone }}>
                    <Icon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
                    <Stack direction="row" alignItems="baseline" gap={1}>
                        <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{value}</Typography>
                        {sub && <Typography variant="caption" color="text.secondary" noWrap>{sub}</Typography>}
                    </Stack>
                </Box>
            </Stack>
        </Card>
    );
}

// userRoles is accepted like the other views; all project data (incl. finance) is visible to every role.
function ProjectsView(_props: ProjectsViewProps) {
    const navigate = useNavigate();
    const theme = useTheme();
    const { projectId } = useParams<{ projectId: string }>();
    const selectedId = projectId ? Number(projectId) : null;

    const [data, setData] = useState<ProjectListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [reload, setReload] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('cards');
    const [numCardsToDisplay, setNumCardsToDisplay] = useState(24);
    const [addOpen, setAddOpen] = useState(false);

    const [scope, setScope] = useState<Scope>('active');
    const [search, setSearch] = useState('');
    const [statuses, setStatuses] = useState<string[]>([]);
    const [conditions, setConditions] = useState<string[]>([]);
    const [clients, setClients] = useState<string[]>([]);
    const [countries, setCountries] = useState<string[]>([]);
    const [people, setPeople] = useState<string[]>([]);
    const [role, setRole] = useState<'all' | 'lead' | 'partner'>('all');
    const [attentionOnly, setAttentionOnly] = useState(false);
    const [sortKey, setSortKey] = useState<SortKey>('project_number');
    const [filtersOpen, setFiltersOpen] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setData(await getData('projects/table') as ProjectListItem[]);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [reload]);

    const options = useMemo(() => {
        const uniq = (values: (string | null)[]) => Array.from(new Set(values.filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b));
        return {
            statuses: uniq(data.map((d) => d.status)),
            conditions: uniq(data.map((d) => d.project_condition)),
            clients: uniq(data.map((d) => d.client)),
            countries: uniq(data.flatMap((d) => splitList(d.countries))),
            people: uniq(data.flatMap((d) => [...splitList(d.project_directors), ...splitList(d.project_managers)])),
        };
    }, [data]);

    const needsAttention = (item: ProjectListItem) =>
        ACTIVE_STATUSES.includes(item.status || '') &&
        ((item.project_condition !== null && item.project_condition !== 'Normal') ||
            (item.months_remaining !== null && item.months_remaining < 0));

    const filteredData = useMemo(() => {
        const query = search.trim().toLowerCase();
        const result = data.filter((item) => {
            if (scope === 'active' && !ACTIVE_STATUSES.includes(item.status || '')) return false;
            if (scope === 'closed' && item.status !== 'Closed') return false;
            if (statuses.length && !statuses.includes(item.status || '')) return false;
            if (conditions.length && !conditions.includes(item.project_condition || '')) return false;
            if (clients.length && !clients.includes(item.client || '')) return false;
            if (countries.length && !splitList(item.countries).some((c) => countries.includes(c))) return false;
            if (people.length && ![...splitList(item.project_directors), ...splitList(item.project_managers)].some((p) => people.includes(p))) return false;
            if (role === 'lead' && !item.lead) return false;
            if (role === 'partner' && item.lead) return false;
            if (attentionOnly && !needsAttention(item)) return false;
            if (query) {
                const haystack = [item.project_number, item.project_name, item.client, item.countries, item.partners,
                    item.project_directors, item.project_managers, item.project_donor_number].join(' ').toLowerCase();
                if (!haystack.includes(query)) return false;
            }
            return true;
        });

        const value = (item: ProjectListItem): string | number => {
            switch (sortKey) {
                case 'project_end_date': return item.project_end_date || '9999';
                case 'int_open_volume': return -(item.int_open_volume ?? 0);
                case 'completion_ratio': return item.completion_ratio ?? 2;
                default: return item.project_number || '';
            }
        };
        return result.sort((a, b) => {
            const va = value(a);
            const vb = value(b);
            if (sortKey === 'project_number') return String(vb).localeCompare(String(va), undefined, { numeric: true });
            return va < vb ? -1 : va > vb ? 1 : 0;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, scope, search, statuses, conditions, clients, countries, people, role, attentionOnly, sortKey]);

    // KPIs always describe the active portfolio, independent of the filters below them.
    const kpis = useMemo(() => {
        const active = data.filter((d) => ACTIVE_STATUSES.includes(d.status || ''));
        const openBy = (currency: string) => active
            .filter((d) => (d.currency || 'EUR') === currency)
            .reduce((sum, d) => sum + Math.max(0, Number(d.int_open_volume ?? 0)), 0);
        return {
            active: active.length,
            ongoing: active.filter((d) => d.status === 'Ongoing').length,
            openEur: openBy('EUR'),
            openUsd: openBy('USD'),
            endingSoon: active.filter((d) => d.months_remaining !== null && d.months_remaining >= 0 && d.months_remaining <= 3).length,
            attention: active.filter(needsAttention).length,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Active detail filters as chips, so they stay visible while the filter panel is closed.
    const activeChips = [
        ...statuses.map((v) => ({ key: `status-${v}`, label: `Status: ${v}`, onDelete: () => setStatuses(statuses.filter((x) => x !== v)) })),
        ...conditions.map((v) => ({ key: `cond-${v}`, label: `Condition: ${v}`, onDelete: () => setConditions(conditions.filter((x) => x !== v)) })),
        ...clients.map((v) => ({ key: `client-${v}`, label: `Client: ${v}`, onDelete: () => setClients(clients.filter((x) => x !== v)) })),
        ...countries.map((v) => ({ key: `country-${v}`, label: `Country: ${v}`, onDelete: () => setCountries(countries.filter((x) => x !== v)) })),
        ...people.map((v) => ({ key: `person-${v}`, label: `PD/PM: ${v}`, onDelete: () => setPeople(people.filter((x) => x !== v)) })),
        ...(role !== 'all' ? [{ key: 'role', label: role === 'lead' ? 'INT lead' : 'INT partner', onDelete: () => setRole('all') }] : []),
        ...(attentionOnly ? [{ key: 'attention', label: 'Needs attention', onDelete: () => setAttentionOnly(false) }] : []),
        ...(search.trim() ? [{ key: 'search', label: `Search: ${search.trim()}`, onDelete: () => setSearch('') }] : []),
    ];
    const activeFilterCount = statuses.length + conditions.length + clients.length + countries.length + people.length + (role !== 'all' ? 1 : 0);

    const openDetails = (id: number) => navigate(`/projects/${id}`);
    const closeDetails = () => navigate('/projects');

    const resetFilters = () => {
        setSearch(''); setStatuses([]); setConditions([]); setClients([]); setCountries([]); setPeople([]);
        setRole('all'); setAttentionOnly(false);
    };

    return (
        <>
            {/* Light grey canvas so the white KPI tiles, filter bar and project cards stand out. */}
            <Box sx={{ backgroundColor: theme.palette.grey[100], minHeight: 'calc(100vh - 64px)', paddingY: 1.5 }}>
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', flexDirection: 'column', transition: 'margin 0.3s ease', marginRight: selectedId ? '45vw' : 0 }}>
                    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <KpiTile Icon={RocketLaunch} label="Active projects" value={String(kpis.active)} sub={`${kpis.ongoing} ongoing`}
                                onClick={() => { resetFilters(); setScope('active'); }} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <KpiTile Icon={Euro} label="Open INT volume" value={formatMoney(kpis.openEur, 'EUR')}
                                sub={kpis.openUsd ? `+ ${formatMoney(kpis.openUsd, 'USD')}` : undefined} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <KpiTile Icon={HourglassBottom} label="Ending within 3 months" value={String(kpis.endingSoon)}
                                onClick={() => { resetFilters(); setScope('active'); setSortKey('project_end_date'); }} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <KpiTile Icon={WarningAmber} label="Need attention" value={String(kpis.attention)} sub="condition or end date"
                                highlight={kpis.attention > 0} onClick={() => { resetFilters(); setScope('active'); setAttentionOnly(true); }} />
                        </Grid>
                    </Grid>

                    <Card elevation={0} sx={{ ...panelSx(theme), marginBottom: 2.5 }}>
                        {/* Always visible: search, scope, filters toggle, sort, view, add */}
                        <Stack direction={{ xs: 'column', lg: 'row' }} alignItems={{ lg: 'center' }} gap={1.5} sx={{ padding: 2 }}>
                            <TextField size="small" placeholder="Search number, name, client, partner, person" value={search}
                                onChange={(e) => setSearch(e.target.value)} sx={{ flexGrow: 1, minWidth: 240 }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                            <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                                <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, value) => value && setScope(value)}>
                                    <ToggleButton value="active">Active</ToggleButton>
                                    <ToggleButton value="closed">Closed</ToggleButton>
                                    <ToggleButton value="all">All incl. history</ToggleButton>
                                </ToggleButtonGroup>
                                <Badge badgeContent={activeFilterCount} color="primary" overlap="rectangular">
                                    <Button size="small" variant={filtersOpen ? 'contained' : 'outlined'} disableElevation startIcon={<Tune />}
                                        endIcon={filtersOpen ? <ExpandLess /> : <ExpandMore />} onClick={() => setFiltersOpen(!filtersOpen)}>
                                        Filters
                                    </Button>
                                </Badge>
                                <FormControl size="small" sx={{ minWidth: 170 }}>
                                    <InputLabel>Sort by</InputLabel>
                                    <Select label="Sort by" value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}>
                                        <MenuItem value="project_number">Project number</MenuItem>
                                        <MenuItem value="project_end_date">End date</MenuItem>
                                        <MenuItem value="int_open_volume">Open INT volume</MenuItem>
                                        <MenuItem value="completion_ratio">% invoiced</MenuItem>
                                    </Select>
                                </FormControl>
                                <ToggleButtonGroup size="small" exclusive value={viewMode} onChange={(_, value) => value && setViewMode(value)}>
                                    <ToggleButton value="cards" aria-label="Cards" title="Cards"><ViewModule fontSize="small" /></ToggleButton>
                                    <ToggleButton value="table" aria-label="Table" title="Table"><TableRows fontSize="small" /></ToggleButton>
                                    <ToggleButton value="timeline" aria-label="Timeline" title="Timeline"><Timeline fontSize="small" /></ToggleButton>
                                </ToggleButtonGroup>
                                <Button size="small" variant="contained" disableElevation startIcon={<Add />} onClick={() => setAddOpen(true)}>
                                    Project
                                </Button>
                            </Stack>
                        </Stack>

                        {/* Expandable detail filters */}
                        <Collapse in={filtersOpen} unmountOnExit>
                            <Divider />
                            <Grid container spacing={2} sx={{ padding: 2 }}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <AutocompleteComponent isMulti label="Status" options={options.statuses} value={statuses} handleChange={(_: any, v: string[]) => setStatuses(v)} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <AutocompleteComponent isMulti label="Condition" options={options.conditions} value={conditions} handleChange={(_: any, v: string[]) => setConditions(v)} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <AutocompleteComponent isMulti label="Client" options={options.clients} value={clients} handleChange={(_: any, v: string[]) => setClients(v)} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <AutocompleteComponent isMulti label="Country" options={options.countries} value={countries} handleChange={(_: any, v: string[]) => setCountries(v)} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <AutocompleteComponent isMulti label="Project Director / Manager" options={options.people} value={people} handleChange={(_: any, v: string[]) => setPeople(v)} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginBottom: 0.5 }}>Integration's role</Typography>
                                    <ToggleButtonGroup size="small" exclusive value={role} onChange={(_, value) => value && setRole(value)} fullWidth>
                                        <ToggleButton value="all">All</ToggleButton>
                                        <ToggleButton value="lead">Lead</ToggleButton>
                                        <ToggleButton value="partner">Partner</ToggleButton>
                                    </ToggleButtonGroup>
                                </Grid>
                            </Grid>
                        </Collapse>

                        {/* Result count + active filters as removable chips */}
                        <Divider />
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ paddingX: 2, paddingY: 1.25, minHeight: 48 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ marginRight: 1 }}>
                                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{filteredData.length}</Box> of {data.length} projects
                            </Typography>
                            {activeChips.map((chip) => (
                                <Chip key={chip.key} size="small" label={chip.label} onDelete={chip.onDelete} />
                            ))}
                            {activeChips.length > 0 && (
                                <Button size="small" onClick={resetFilters}>Clear all</Button>
                            )}
                        </Stack>
                    </Card>

                    <div id="back-to-top-anchor" />
                    <ScrollTop>
                        <Fab color="primary" size="small" aria-label="scroll back to top">
                            <KeyboardArrowUp />
                        </Fab>
                    </ScrollTop>

                    {loading && data.length === 0 ? (
                        <Grid container spacing={2}>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Grid item xs={12} md={6} key={i}><Skeleton variant="rounded" height={260} /></Grid>
                            ))}
                        </Grid>
                    ) : viewMode === 'timeline' ? (
                        <ProjectTimeline items={filteredData} onDetailsClick={openDetails} />
                    ) : viewMode === 'table' ? (
                        <Card elevation={0} sx={{ ...panelSx(theme), overflow: 'hidden' }}>
                            <DataGrid
                                rows={filteredData}
                                columns={tableColumns}
                                getRowId={(row) => row.ID}
                                density="compact"
                                onRowClick={(params) => openDetails(params.row.ID)}
                                initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                                pageSizeOptions={[25, 50, 100]}
                                hideFooterSelectedRowCount
                                // Fixed height: the grid scrolls inside itself, so the header row stays visible (sticky).
                                sx={{
                                    height: 'calc(100vh - 150px)', minHeight: 420, cursor: 'pointer', border: 0,
                                    '& .MuiDataGrid-columnHeaders': { backgroundColor: 'action.hover' },
                                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                    // rows per page + page arrows on the outer left
                                    '& .MuiDataGrid-footerContainer': { justifyContent: 'flex-start' },
                                    '& .MuiTablePagination-root': { marginRight: 'auto' },
                                    '& .MuiTablePagination-toolbar': { paddingLeft: 1 },
                                    '& .MuiTablePagination-spacer': { display: 'none' },
                                }}
                            />
                        </Card>
                    ) : (
                        <Grid container spacing={2.5}>
                            {filteredData.slice(0, numCardsToDisplay).map((item) => (
                                <Grid item xs={12} md={selectedId ? 12 : 6} key={item.ID}>
                                    <ProjectCard item={item} onDetailsClick={openDetails} />
                                </Grid>
                            ))}
                            {filteredData.length > numCardsToDisplay && (
                                <Grid item xs={12}>
                                    <Box display="flex" justifyContent="center" paddingY={6}>
                                        <Button startIcon={<ArrowForward />} variant="outlined" onClick={() => setNumCardsToDisplay(numCardsToDisplay + 24)}>
                                            Load more projects
                                        </Button>
                                    </Box>
                                </Grid>
                            )}
                            {filteredData.length === 0 && (
                                <Grid item xs={12}>
                                    <Typography color="text.secondary" textAlign="center" paddingY={6}>No projects match these filters.</Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Box>
            </Container>
            </Box>

            {selectedId !== null && !Number.isNaN(selectedId) && (
                <ProjectDetails projectId={selectedId} onClose={closeDetails} setLoading={setLoading} onSaved={() => setReload((r) => !r)} />
            )}

            <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
                <AddProject
                    onClose={() => setAddOpen(false)}
                    onCreated={(id: number) => { setAddOpen(false); setReload((r) => !r); openDetails(id); }}
                />
            </Dialog>

            <Backdrop open={loading && data.length > 0} style={{ zIndex: 9999, color: '#fff' }}>
                <Fade in={loading && data.length > 0}>
                    <img src={theme.logo} alt="Company Logo" css={pulseAnimation} />
                </Fade>
            </Backdrop>
        </>
    );
}

export default ProjectsView;
